import React, { useState } from 'react';
import { bool, func, node, number, oneOf, oneOfType, string } from 'prop-types';

import { Text } from '@chaser/components';
import classnames from 'classnames';
import { detect } from 'detect-browser';
import { isString } from 'lodash';
import style from './ChaserInput.module.scss';

const browser = detect();

const preventDefaultNumberChars = e => {
  let checkIfNum;
  const validKeyCodes = [
    8, // backspace
    46, // delete
    37, // left arrow
    38, // up arrow
    39, // right arrow
    40, // down arrow
    190, // decimal point
    110, // numpad decimal
  ];
  const numpadKeyCode = Array.from({ length: 10 }, (_, i) => i + 96); // 96-105 for numpad numbers

  if (
    e.keyCode &&
    (e.keyCode < 48 || e.keyCode > 57) &&
    !validKeyCodes.some(kc => kc === e.keyCode) &&
    !numpadKeyCode.includes(e.keyCode)
  ) {
    checkIfNum = true;
  } else if (e.key !== undefined) {
    checkIfNum = e.key === 'e' || e.key === '+' || e.key === '-';
  } else if (e.keyCode !== undefined) {
    checkIfNum = e.keyCode === 69 || e.keyCode === 187 || e.keyCode === 189;
  }
  return checkIfNum && e.preventDefault();
};

const ChaserInput = ({
  currencySymbol,
  formatCurrencyValue,
  className = '',
  wrapperClassName = '',
  value,
  icon,
  onChange = () => {},
  placeholder = '',
  onBlur,
  error,
  type = 'text',
  width,
  height = 'medium',
  ...rest
}) => {
  const [isEditing, setIsEditing] = useState(false);

  function handleBlur(e) {
    setIsEditing(false);
    if (onBlur) onBlur(e);
  }

  let input;
  if (currencySymbol && isEditing) {
    const isFirefox = browser && browser.name === 'firefox';
    const inputType = isFirefox ? 'text' : 'number';
    input = (
      <input
        {...rest}
        type={inputType}
        value={value}
        placeholder={placeholder}
        onKeyDown={preventDefaultNumberChars}
        onChange={onChange}
        className={classnames(
          style.input,
          style[height],
          style.icon,
          className,
          error && style.error,
        )}
        onBlur={handleBlur}
        min="0.00"
        step="0.01"
      />
    );
  } else if (currencySymbol && !isEditing) {
    input = (
      <input
        {...rest}
        type="text"
        placeholder={placeholder}
        value={formatCurrencyValue(value)}
        className={classnames(
          style.input,
          style[height],
          style.icon,
          className,
          error && style.error,
        )}
        onFocus={() => setIsEditing(true)}
        readOnly
      />
    );
  } else {
    const Comp = type === 'textarea' ? 'textarea' : 'input';
    input = (
      <Comp
        type={type}
        placeholder={placeholder}
        className={classnames(
          style.input,
          style[height],
          icon && style.icon,
          error && style.error,
          className,
          style[type],
        )}
        onChange={onChange}
        value={value}
        onBlur={handleBlur}
        {...rest}
      />
    );
  }

  return (
    <div className={classnames(style.wrapper, wrapperClassName, style[width])}>
      <div className={style.inputWrapper}>
        {(currencySymbol || icon) && (
          <span className={style.searchIcon}>
            {currencySymbol && !icon && (
              <span
                className={classnames(
                  style.currencySymbol,
                  /^[A-Z]{3}$/.test(currencySymbol) && style.code,
                )}
              >
                {currencySymbol}
              </span>
            )}
            {icon && (typeof icon === 'string' ? <img src={icon} alt="" /> : icon)}
          </span>
        )}
        {input}
      </div>
      {isString(error) && (
        <Text className={style.errorLabel} fontSize="xsmall" color="Red">
          {error}
        </Text>
      )}
    </div>
  );
};

export default ChaserInput;

ChaserInput.propTypes = {
  currencySymbol: string,
  formatCurrencyValue: func,
  className: string,
  wrapperClassName: string,
  value: oneOfType([string, number]),
  placeholder: string,
  onChange: func,
  icon: node,
  onBlur: func,
  error: oneOfType([bool, string]),
  type: oneOf(['text', 'submit', 'email', 'password', 'number', 'tel', 'textarea']),
  width: oneOf(['full']),
  height: oneOf(['small', 'medium']),
};
